import React, { useEffect } from 'react';
import { Modal, Form, Input, Select, Space, Button, Typography } from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { PLATFORM_OPTIONS, REGION_OPTIONS, IOS_REGION_OPTIONS } from './materialConfig';

const { Text } = Typography;

/**
 * 定制申请 Modal（企业端）
 * - 无 project：发起定制（提交申请 → 待审核）
 * - 有 project：驳回后编辑申请信息并重新提交
 */
const ApplyModal = ({ open, project, onCancel, onSubmit }) => {
    const [form] = Form.useForm();
    const targets = Form.useWatch('targets', form) || [];
    const isEdit = !!project;
    const usedPlatforms = targets.map((t) => t?.platform).filter(Boolean);

    useEffect(() => {
        if (open) {
            form.setFieldsValue({
                appName: project?.appName || '',
                targets: project?.targets?.length ? project.targets : [{ regions: [] }],
            });
        }
    }, [open, project, form]);

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            onSubmit(values);
        } catch (e) {
            if (e?.errorFields) return;
        }
    };

    return (
        <Modal
            title={isEdit ? '编辑定制申请' : '发起 OEM App 定制'}
            open={open}
            onOk={handleOk}
            onCancel={() => { onCancel(); form.resetFields(); }}
            okText={isEdit ? '重新提交申请' : '提交申请'}
            cancelText="取消"
            width={560}
            destroyOnClose
        >
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
                提交后需平台客服审核通过，方可开始填写资料。
            </Text>
            <Form form={form} layout="vertical" preserve={false}>
                <Form.Item name="appName" label="应用名称" rules={[{ required: true, message: '请输入应用名称' }]}>
                    <Input placeholder="请输入 App 名称" maxLength={30} />
                </Form.Item>

                <div style={{ marginBottom: 8 }}>
                    <Text strong style={{ fontSize: 13 }}>定制范围（平台 + 上架区域）</Text>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                        每个平台可关联一个或多个上架区域；系统将按此动态加载所需资料。
                    </Text>
                </div>
                <Form.List name="targets"
                    rules={[{ validator: async (_, value) => { if (!value || !value.length) throw new Error('请至少配置一个平台'); } }]}>
                    {(fields, { add, remove }, { errors }) => (
                        <>
                            {fields.map(({ key, name, ...restField }) => {
                                const currentPlatform = targets[name]?.platform;
                                const isIOS = currentPlatform === 'ios';
                                const disabledPlatforms = usedPlatforms.filter((p) => p !== currentPlatform);
                                return (
                                    <Space key={key} align="baseline" style={{ display: 'flex', marginBottom: 4 }}>
                                        <Form.Item {...restField} name={[name, 'platform']} rules={[{ required: true, message: '请选择平台' }]}>
                                            <Select placeholder="平台" style={{ width: 140 }}
                                                onChange={() => form.setFieldValue(['targets', name, 'regions'], undefined)}
                                                options={PLATFORM_OPTIONS.map((o) => ({ ...o, disabled: disabledPlatforms.includes(o.value) }))} />
                                        </Form.Item>
                                        <Form.Item {...restField} name={[name, 'regions']} rules={[{ required: true, message: '请选择上架区域' }]}>
                                            {isIOS
                                                ? <Select placeholder="上架区域（单选：全球通用 / 国内 / 海外）" style={{ width: 300 }} options={IOS_REGION_OPTIONS} />
                                                : <Select mode="multiple" placeholder="上架区域（可多选）" style={{ width: 300 }} options={REGION_OPTIONS} />}
                                        </Form.Item>
                                        {fields.length > 1 && (
                                            <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#999' }} />
                                        )}
                                    </Space>
                                );
                            })}
                            <Form.Item style={{ marginBottom: 0 }}>
                                <Button type="dashed" block icon={<PlusOutlined />}
                                    disabled={fields.length >= PLATFORM_OPTIONS.length}
                                    onClick={() => add({ regions: [] })}>
                                    添加平台配置
                                </Button>
                                <Form.ErrorList errors={errors} />
                            </Form.Item>
                        </>
                    )}
                </Form.List>
            </Form>
        </Modal>
    );
};

export default ApplyModal;
